// Where the page talks to the backend. Resolves at runtime so a
// single static bundle can be served from three different
// topologies without any of them inheriting the others' host:
//
//   - controllerd  : same-origin (/api, /ws on the controller).
//   - Cloudflare prod (design.helm-ux.com): same-origin via
//     zone-routed /api → helm-api worker.
//   - Cloudflare preview (*.helm-design.workers.dev): cross-origin
//     to https://api.helm-ux.com, injected via window.HELM_API_HOST
//     by the preview deployment.
//
// Resolution order:
//
//   1. window.HELM_API_HOST            (string, no trailing slash)
//   2. import.meta.env.VITE_API_HOST   (Vite build-time fallback)
//   3. same-origin                     (empty string)
//
// The default — empty — must be the controllerd answer; nothing
// about Cloudflare leaks into a controllerd install unless the
// install itself opted in by injecting window.HELM_API_HOST.

declare global {
  interface Window {
    HELM_API_HOST?: string;
  }
}

function readWindowHost(): string | null {
  if (typeof globalThis === 'undefined') return null;
  const w = globalThis as { HELM_API_HOST?: unknown };
  return typeof w.HELM_API_HOST === 'string' && w.HELM_API_HOST.length > 0
    ? w.HELM_API_HOST
    : null;
}

function readBuildHost(): string | null {
  // Vite replaces `import.meta.env.VITE_API_HOST` at build time.
  // Outside Vite (vitest, node tests) the property is undefined and
  // the lookup just returns null. Wrapped in a try so node builds
  // without `import.meta.env` don't blow up at module init.
  try {
    const v = (import.meta as unknown as { env?: Record<string, string | undefined> })
      .env;
    const host = v?.VITE_API_HOST;
    return typeof host === 'string' && host.length > 0 ? host : null;
  } catch {
    return null;
  }
}

function stripTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

/**
 * Resolve the HTTP origin the page should hit for `/api/*`. Returns
 * an empty string when the answer is "same-origin" — callers that
 * concatenate `origin + '/api/foo'` get a relative URL in that case,
 * which is exactly what `fetch` wants for same-origin requests.
 */
export function apiOrigin(): string {
  const explicit = readWindowHost() ?? readBuildHost();
  if (!explicit) return '';
  return stripTrailingSlash(explicit);
}

/**
 * Resolve the WebSocket URL for the live protocol. Inherits the
 * scheme (ws / wss) from the resolved HTTP origin, or from
 * `window.location` when the answer is same-origin.
 *
 * Returns null when no `lab_id` is on the page URL — the
 * dashboard / kiosk refuse to attempt a connection without one.
 * Callers in IPC mode bypass this helper entirely (they use
 * `ipc://broker` against the in-browser transport).
 */
export function deriveWsUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const labId = new URLSearchParams(window.location.search).get('lab_id');
  if (!labId) return null;
  const explicit = apiOrigin();
  if (explicit) {
    const wsBase = explicit.replace(/^http(s?):\/\//i, (_, s) => `ws${s}://`);
    return `${stripTrailingSlash(wsBase)}/ws?lab_id=${encodeURIComponent(labId)}`;
  }
  // Same-origin fallback. Scheme follows the page.
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws?lab_id=${encodeURIComponent(labId)}`;
}

/**
 * Like `deriveWsUrl` but for the Designer, which may run against
 * the SW broker (no `lab_id` required) or the controller. Returns
 * a URL even when `lab_id` is absent so the SW's same-origin /ws
 * intercept still fires; the broker doesn't need a lab handle.
 */
export function deriveDesignerWsUrl(): string {
  if (typeof window === 'undefined') return 'ws://127.0.0.1/ws';
  const labId = new URLSearchParams(window.location.search).get('lab_id') ?? '';
  const qs = labId ? `?lab_id=${encodeURIComponent(labId)}` : '';
  const explicit = apiOrigin();
  if (explicit) {
    const wsBase = explicit.replace(/^http(s?):\/\//i, (_, s) => `ws${s}://`);
    return `${stripTrailingSlash(wsBase)}/ws${qs}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws${qs}`;
}
